// «Κρατήσεις» μέσα στο ταμείο: η καθημερινή δουλειά χωρίς να φεύγει κανείς από το ταμείο.
// Λίστα της ημέρας ανά ώρα, νέα κράτηση/αλλαγή, επιβεβαίωση, «Ήρθαν», «Κάθισε» σε τραπέζι
// (ανοίγει λογαριασμό), ακύρωση, no-show. Ρυθμίσεις, πελατολόγιο, αναφορές: στη Sala.
// Ίδιο API και ίδιοι κανόνες με τη Sala (/api/reservations), ζωντανές αλλαγές με socket.
// Προκαταβολές στο ταμείο (components/deposits): μόνο με ανοιχτό τον διακόπτη του καταστήματος.

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  AlertTriangle, CalendarDays, Check, ChevronLeft, ChevronRight, Clock, Mail, Phone, Plus, Search, Star, UserCheck, Users, X,
} from 'lucide-react';
import { useToast } from '../../shared/Toast';
import { useSocket } from '../../../hooks/useSocket';
import { usePermissions } from '../../../hooks/usePermissions';
import { DepositChip, ReservationDepositSection, useCounterDepositsByReservation } from '../../deposits/ReservationDeposit';
import { useDepositsConfig } from '../../deposits/useDepositsConfig';
import type { DepositsConfig } from '../../deposits/types';
import { athensMinutes } from '../../../utils/dineInReservations';
import { Btn, Note } from '../floorEditor/ui';
import { ResError, resApi } from './api';
import { addDays, athensToday, byTime, counts, isLate, matches, occupied, otherActions, primaryAction, seatOrder, tablesOf, type Filter } from './model';
import ReservationForm from './ReservationForm';
import { useK } from './useK';
import type { Day, Reservation, ResAction } from './types';

const STATUS_LOOK: Record<string, string> = {
  pending: 'bg-amber-500/12 text-amber-700 dark:text-amber-300',
  confirmed: 'bg-blue-500/12 text-blue-700 dark:text-blue-300',
  waitlist: 'bg-surface-3 text-t2',
  arrived: 'bg-violet-500/12 text-violet-700 dark:text-violet-300',
  seated: 'bg-emerald-500/12 text-emerald-700 dark:text-emerald-300',
  completed: 'bg-surface-3 text-t3',
  cancelled: 'bg-surface-3 text-t3 line-through',
  no_show: 'bg-red-500/12 text-red-700 dark:text-red-300',
  late: 'bg-red-500/12 text-red-700 dark:text-red-300',
};

export default function ReservationsScreen({ storeId, onClose, onOpenTable }: {
  storeId: number; onClose: () => void; onOpenTable: (tableId: number) => void;
}) {
  const { k, dayLabel, errText } = useK();
  const { showToast } = useToast();
  const { socket } = useSocket();
  const today = athensToday();
  const [date, setDate] = useState(today);
  const [day, setDay] = useState<Day | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState<Filter>('active');
  const [q, setQ] = useState('');
  const [selId, setSelId] = useState<number | null>(null);
  const [form, setForm] = useState<{ editing?: Reservation } | null>(null);
  const [seating, setSeating] = useState<Reservation | null>(null);
  const [busyId, setBusyId] = useState<number | null>(null);
  const [nowMin, setNowMin] = useState(athensMinutes());
  const timer = useRef<number | null>(null);
  // Προκαταβολές ταμείου: ο διακόπτης του καταστήματος (κλειστός = τίποτα στην οθόνη) και οι
  // προκαταβολές των κρατήσεων, ξανά σε κάθε φόρτωμα της ημέρας (depTick).
  const deposits = useDepositsConfig(storeId);
  const { hasPermission } = usePermissions();
  const canCollect = hasPermission('dine_in_process_payment');
  const [depTick, setDepTick] = useState(0);
  const depositsByRes = useCounterDepositsByReservation(storeId, deposits.enabled, depTick);

  const load = useCallback(async (d = date) => {
    try {
      const next = await resApi.day(storeId, d);
      setDay(next);
      setDepTick(n => n + 1);
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  }, [storeId, date]);

  useEffect(() => { setDay(null); setSelId(null); load(date); }, [date]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { const id = window.setInterval(() => setNowMin(athensMinutes()), 30000); return () => window.clearInterval(id); }, []);
  // Ζωντανά: ό,τι αλλάξει στη Sala, σε άλλο ταμείο ή από τον πελάτη (online) φαίνεται εδώ.
  useEffect(() => {
    if (!socket) return;
    const refresh = () => {
      if (timer.current) window.clearTimeout(timer.current);
      timer.current = window.setTimeout(() => load(), 300);
    };
    socket.on('reservation:changed', refresh);
    socket.on('table-update', refresh);
    return () => { socket.off('reservation:changed', refresh); socket.off('table-update', refresh); };
  }, [socket, load]);
  // Esc κλείνει την οθόνη (όταν δεν είναι ανοιχτή φόρμα ή επιλογή τραπεζιού).
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape' && !form && !seating) onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [form, seating, onClose]);

  const list = useMemo(() => (day?.reservations || []).filter(r => matches(r, filter, q)), [day, filter, q]);
  const groups = useMemo(() => byTime(list), [list]);
  const c = useMemo(() => counts(day?.reservations || []), [day]);
  const sel = day?.reservations.find(r => r.id === selId) || null;
  const tableNo = useCallback((id: number) => day?.tables.find(t => t.id === id)?.table_number ?? '?', [day]);
  const tablesLabel = (r: Reservation) => {
    const ids = tablesOf(r);
    return ids.length ? ids.map(id => `${k('tablePrefix')}${tableNo(id)}`).join('+') : k('row.noTable');
  };
  const who = (r: Reservation) => r.customer_name.trim();

  const run = async (r: Reservation, action: ResAction, extra: { table_ids?: number[]; force?: boolean; notify?: string } = {}) => {
    setBusyId(r.id);
    try {
      await resApi.act(storeId, r.id, action, extra);
      const tab = extra.table_ids?.[0] ?? r.table_id;
      showToast('success', k(`done.${action}`, { name: who(r), table: tab ? `${k('tablePrefix')}${tableNo(tab)}` : '' }));
      await load();
      return true;
    } catch (e) {
      if (action === 'seat' && e instanceof ResError && e.status === 409) throw e;
      showToast('error', errText(e));
      return false;
    } finally {
      setBusyId(null);
    }
  };

  const seat = async (r: Reservation) => {
    if (!day) return;
    const mine = tablesOf(r);
    const busy = occupied(day);
    // Έχει τραπέζι που είναι ελεύθερο τώρα: κάθεται εκεί. Αλλιώς διαλέγει ο ταμίας.
    if (date === today && mine.length && !mine.some(id => busy.has(id))) {
      try { await run(r, 'seat', { table_ids: mine }); } catch { setSeating(r); }
      return;
    }
    setSeating(r);
  };

  const doPrimary = (r: Reservation) => {
    const a = primaryAction(r);
    if (a === 'seat') seat(r);
    else if (a) run(r, a);
  };

  return (
    <div className="fixed inset-0 z-50 flex flex-col bg-surface-0" role="dialog" aria-modal="true" aria-label={k('title')}>
      <header className="flex flex-wrap items-center gap-2 border-b border-line bg-surface-1 px-3 py-2.5">
        <Btn variant="ghost" size="icon" aria-label={k('close')} onClick={onClose}><X className="h-5 w-5" /></Btn>
        <h2 className="mr-2 flex items-center gap-2 text-[19px] font-bold text-t1"><CalendarDays className="h-5 w-5 text-blue-600" />{k('title')}</h2>
        <div className="flex items-center gap-1">
          <Btn variant="ghost" size="icon" aria-label={k('day.prev')} onClick={() => setDate(addDays(date, -1))}><ChevronLeft className="h-5 w-5" /></Btn>
          <button type="button" onClick={() => setDate(today)} className="h-10 min-w-[120px] rounded-lg px-3 text-[15px] font-semibold text-t1 hover:bg-surface-2">{dayLabel(date, today)}</button>
          <Btn variant="ghost" size="icon" aria-label={k('day.next')} onClick={() => setDate(addDays(date, 1))}><ChevronRight className="h-5 w-5" /></Btn>
          <input type="date" value={date} onChange={e => e.target.value && setDate(e.target.value)} aria-label={k('day.pick')}
            className="h-10 rounded-lg border border-line bg-surface-2 px-2 text-[13px] text-t1" />
        </div>
        <label className="relative ml-auto flex min-w-[200px] max-w-[320px] flex-1 items-center">
          <Search className="pointer-events-none absolute left-3 h-4 w-4 text-t3" />
          <input value={q} onChange={e => setQ(e.target.value)} placeholder={k('search')}
            className="h-10 w-full rounded-lg border border-line bg-surface-2 pl-9 pr-3 text-[14px] text-t1 placeholder:text-t4 focus:border-blue-500 focus:outline-none" />
        </label>
        <Btn variant="primary" icon={<Plus className="h-4 w-4" />} onClick={() => setForm({})}>{k('new')}</Btn>
      </header>

      <div className="flex flex-wrap items-center gap-2 border-b border-line bg-surface-1 px-4 py-2">
        <div className="flex gap-1 rounded-lg bg-surface-2 p-1">
          {(['active', 'seated', 'all'] as Filter[]).map(f => (
            <button key={f} type="button" aria-pressed={filter === f} onClick={() => setFilter(f)}
              className={`h-8 rounded-md px-3 text-[13px] font-medium ${filter === f ? 'bg-surface-1 text-t1 shadow-sm' : 'text-t3 hover:text-t1'}`}>{k(`filter.${f}`)}</button>
          ))}
        </div>
        <span className="ml-1 text-[13px] text-t3">
          {k('sum.bookings', { count: c.bookings })} · {k('sum.covers', { count: c.covers })} · {k('sum.coming', { count: c.coming })} · {k('sum.seated', { count: c.seated })}
        </span>
        {c.pending > 0 && <span className="rounded-full bg-amber-500/12 px-2.5 py-0.5 text-[12.5px] font-semibold text-amber-700 dark:text-amber-300">{k('sum.pending', { count: c.pending })}</span>}
      </div>

      <div className="flex min-h-0 flex-1">
        <div className="min-w-0 flex-1 overflow-y-auto px-4 py-3">
          {error ? (
            <div className="grid place-items-center gap-3 py-16 text-center"><p className="text-t2">{k('loadFailed', { error })}</p><Btn onClick={() => load()}>{k('retry')}</Btn></div>
          ) : !day ? (
            <div className="grid gap-2">{Array.from({ length: 6 }, (_, i) => <div key={i} className="h-16 animate-pulse rounded-xl bg-surface-2" />)}</div>
          ) : groups.length === 0 ? (
            <div className="grid place-items-center gap-3 py-16 text-center">
              <CalendarDays className="h-10 w-10 text-t4" />
              <p className="text-t2">{q ? k('emptySearch') : k('empty')}</p>
              {!q && <Btn variant="primary" icon={<Plus className="h-4 w-4" />} onClick={() => setForm({})}>{k('new')}</Btn>}
            </div>
          ) : (
            <div className="grid gap-4">
              {groups.map(g => (
                <section key={g.time} className="grid gap-1.5">
                  <h3 className="flex items-baseline gap-2 px-1 text-[13px] text-t3">
                    <span className="text-[16px] font-bold tabular-nums text-t1">{g.time}</span>
                    {k('sum.bookings', { count: g.items.length })} · {k('sum.covers', { count: g.items.reduce((s, r) => s + r.party_size, 0) })}
                  </h3>
                  <ul className="grid gap-1.5">
                    {g.items.map(r => {
                      const late = isLate(r, today, nowMin);
                      const prim = primaryAction(r);
                      const allergy = r.allergies || r.guest_allergies;
                      return (
                        <li key={r.id}>
                          <div role="button" tabIndex={0} onClick={() => setSelId(r.id)} onKeyDown={e => { if (e.key === 'Enter') setSelId(r.id); }}
                            className={`grid cursor-pointer grid-cols-[minmax(0,1fr)_auto_auto_auto] items-center gap-3 rounded-xl border px-3.5 py-2.5 transition-colors ${selId === r.id ? 'border-blue-500 bg-blue-500/5' : 'border-line bg-surface-1 hover:border-line-strong'} ${['cancelled', 'no_show', 'completed'].includes(r.status) ? 'opacity-60' : ''}`}>
                            <div className="min-w-0">
                              <div className="flex items-center gap-1.5">
                                <span className="truncate text-[15px] font-semibold text-t1">{r.customer_name}</span>
                                {r.guest_is_vip && <Star className="h-4 w-4 shrink-0 fill-amber-400 text-amber-500" />}
                                {allergy && <AlertTriangle className="h-4 w-4 shrink-0 text-red-600" />}
                                {r.source === 'online' && <span className="rounded bg-surface-3 px-1.5 text-[11px] font-semibold text-t3">online</span>}
                              </div>
                              {(r.notes || (r.tags && r.tags.length > 0)) && (
                                <div className="truncate text-[12.5px] text-t3">{[...(r.tags || []), r.notes].filter(Boolean).join(' · ')}</div>
                              )}
                              {deposits.enabled && <div className="mt-0.5 flex min-w-0"><DepositChip r={r} views={depositsByRes.get(r.id)} /></div>}
                            </div>
                            <span className="flex items-center gap-1 text-[14px] font-semibold tabular-nums text-t2"><Users className="h-4 w-4 text-t3" />{r.party_size}</span>
                            <span className={`min-w-[64px] text-right text-[14px] font-semibold ${tablesOf(r).length ? 'text-t1' : 'text-t4'}`}>{tablesLabel(r)}</span>
                            <div className="flex items-center gap-2">
                              <span className={`whitespace-nowrap rounded-full px-2.5 py-0.5 text-[12px] font-semibold ${STATUS_LOOK[late ? 'late' : r.status]}`}>
                                {late ? k('status.late', { min: nowMin - Number(r.reservation_time.slice(0, 2)) * 60 - Number(r.reservation_time.slice(3, 5)) }) : k(`status.${r.status}`)}
                              </span>
                              {prim && (
                                <Btn size="sm" variant={prim === 'seat' ? 'primary' : 'secondary'} disabled={busyId === r.id}
                                  onClick={e => { e.stopPropagation(); doPrimary(r); }}>{k(`action.${prim}`)}</Btn>
                              )}
                            </div>
                          </div>
                        </li>
                      );
                    })}
                  </ul>
                </section>
              ))}
            </div>
          )}
        </div>

        {sel && (
          <Detail r={sel} today={today} busy={busyId === sel.id} tablesLabel={tablesLabel(sel)}
            deposit={deposits.enabled && deposits.config
              ? { storeId, config: deposits.config, canCollect, refreshKey: depTick, onChanged: () => load() }
              : null}
            onClose={() => setSelId(null)} onEdit={() => setForm({ editing: sel })}
            onPrimary={() => doPrimary(sel)} onAction={(a, extra) => (a === 'seat' ? seat(sel) : run(sel, a, extra))}
            onOpenTable={sel.status === 'seated' && sel.table_id ? () => { onOpenTable(sel.table_id!); } : undefined} />
        )}
      </div>

      {seating && day && (
        <SeatPicker r={seating} day={day} onClose={() => setSeating(null)}
          onPick={async (ids, force) => {
            try {
              const ok = await run(seating, 'seat', { table_ids: ids, force });
              if (ok) setSeating(null);
              return ok ? 'ok' : 'error';
            } catch (e) {
              return e instanceof ResError ? errText(e) : 'error';
            }
          }} />
      )}
      {form && (
        <ReservationForm storeId={storeId} date={date} today={today} editing={form.editing} tableNo={tableNo}
          onClose={() => setForm(null)} onSaved={async saved => { setForm(null); await load(); if (saved) setSelId(saved.id); }} />
      )}
    </div>
  );
}

function Detail({ r, today, busy, tablesLabel, deposit, onClose, onEdit, onPrimary, onAction, onOpenTable }: {
  r: Reservation; today: string; busy: boolean; tablesLabel: string;
  /** Προκαταβολές ταμείου — null όταν ο διακόπτης είναι κλειστός. */
  deposit: { storeId: number; config: DepositsConfig; canCollect: boolean; refreshKey: unknown; onChanged: () => void } | null;
  onClose: () => void; onEdit: () => void; onPrimary: () => void;
  onAction: (a: ResAction, extra?: { notify?: string }) => void; onOpenTable?: () => void;
}) {
  const { k, dayLabel } = useK();
  const [confirm, setConfirm] = useState<ResAction | null>(null);
  useEffect(() => setConfirm(null), [r.id]);
  const prim = primaryAction(r);
  const others = otherActions(r, today);
  const allergy = r.allergies || r.guest_allergies;
  const editable = ['pending', 'confirmed', 'waitlist', 'arrived'].includes(r.status);
  return (
    <aside className="flex w-[380px] shrink-0 flex-col border-l border-line bg-surface-1">
      <div className="flex items-start gap-2 border-b border-line px-4 py-4">
        <div className="min-w-0 flex-1">
          <h3 className="flex items-center gap-1.5 truncate text-[20px] font-bold text-t1">
            {r.customer_name}{r.guest_is_vip && <Star className="h-4 w-4 fill-amber-400 text-amber-500" />}
          </h3>
          <p className="mt-0.5 text-[13px] text-t3">
            {dayLabel(r.reservation_date, today)} · {r.reservation_time.slice(0, 5)} · {k('people', { count: r.party_size })} · {tablesLabel}
          </p>
        </div>
        <Btn size="icon" aria-label={k('close')} onClick={onClose}><X className="h-4 w-4" /></Btn>
      </div>
      <div className="grid min-h-0 flex-1 auto-rows-max content-start gap-3 overflow-y-auto px-4 py-4">
        <span className={`justify-self-start rounded-full px-2.5 py-0.5 text-[12.5px] font-semibold ${STATUS_LOOK[r.status]}`}>{k(`status.${r.status}`)}</span>
        {allergy && <Note tone="error">{k('detail.allergies', { text: allergy })}</Note>}
        <dl className="grid gap-2 text-[14px]">
          {r.customer_phone && <Row icon={<Phone className="h-4 w-4" />} text={<a className="text-blue-600 hover:underline" href={`tel:${r.customer_phone}`}>{r.customer_phone}</a>} />}
          {r.customer_email && <Row icon={<Mail className="h-4 w-4" />} text={r.customer_email} />}
          <Row icon={<Clock className="h-4 w-4" />} text={k('detail.duration', { min: r.duration_minutes })} />
          {(r.guest_visits ?? 0) > 0 && <Row icon={<UserCheck className="h-4 w-4" />} text={k('detail.visits', { count: r.guest_visits ?? 0 })} />}
          {(r.guest_no_shows ?? 0) > 0 && <Row icon={<AlertTriangle className="h-4 w-4 text-red-600" />} text={k('detail.noShows', { count: r.guest_no_shows ?? 0 })} />}
        </dl>
        {r.tags && r.tags.length > 0 && <div className="flex flex-wrap gap-1.5">{r.tags.map(t => <span key={t} className="rounded-full bg-surface-3 px-2.5 py-0.5 text-[12.5px] text-t2">{t}</span>)}</div>}
        {r.notes && <Note>{r.notes}</Note>}
        {r.guest_notes && <p className="text-[13px] text-t3">{k('detail.guestNotes', { text: r.guest_notes })}</p>}
        {deposit && (
          <ReservationDepositSection storeId={deposit.storeId} r={r} config={deposit.config} canCollect={deposit.canCollect}
            refreshKey={deposit.refreshKey} onChanged={deposit.onChanged} />
        )}
        <p className="text-[12px] text-t4">{k(`source.${['online', 'phone', 'walk_in', 'staff'].includes(r.source) ? r.source : 'other'}`)}{r.code ? ` · ${k('detail.code', { code: r.code })}` : ''}</p>
      </div>
      <div className="grid gap-2 border-t border-line px-4 py-3">
        {confirm ? (
          <div className="grid gap-2 rounded-lg bg-red-500/8 p-3">
            <p className="text-[14px] font-semibold text-t1">{k(`confirm.${confirm}`)}</p>
            <div className="flex gap-2">
              <Btn variant="danger" disabled={busy} onClick={() => { onAction(confirm); setConfirm(null); }}>{k('confirm.yes')}</Btn>
              <Btn variant="ghost" onClick={() => setConfirm(null)}>{k('confirm.no')}</Btn>
            </div>
          </div>
        ) : (
          <>
            {prim && <Btn variant="primary" className="h-12 justify-center text-[15px]" icon={<Check className="h-4 w-4" />} disabled={busy} onClick={onPrimary}>{k(`action.${prim}`)}</Btn>}
            {onOpenTable && <Btn variant="primary" className="h-12 justify-center text-[15px]" onClick={onOpenTable}>{k('detail.openTable')}</Btn>}
            <div className="flex flex-wrap gap-1.5">
              {editable && <Btn size="sm" onClick={onEdit}>{k('action.edit')}</Btn>}
              {others.map(a => (
                <Btn key={a} size="sm" variant={a === 'cancel' || a === 'no-show' ? 'danger' : 'secondary'} disabled={busy}
                  onClick={() => (a === 'cancel' || a === 'no-show' ? setConfirm(a) : onAction(a))}>{k(`action.${a}`)}</Btn>
              ))}
            </div>
          </>
        )}
      </div>
    </aside>
  );
}

function Row({ icon, text }: { icon: React.ReactNode; text: React.ReactNode }) {
  return <div className="flex items-center gap-2 text-t2"><span className="text-t3">{icon}</span><span className="min-w-0 truncate">{text}</span></div>;
}

/** «Κάθισε»: τα τραπέζια του καταστήματος, ελεύθερα και κατάλληλα πρώτα. */
function SeatPicker({ r, day, onClose, onPick }: {
  r: Reservation; day: Day; onClose: () => void; onPick: (ids: number[], force?: boolean) => Promise<'ok' | 'error' | string>;
}) {
  const { k } = useK();
  const busy = useMemo(() => occupied(day), [day]);
  const order = useMemo(() => seatOrder(day.tables, r.party_size, busy, tablesOf(r)), [day, r, busy]);
  const zoneName = (id: number) => day.zones.find(z => z.id === id)?.name || '';
  const [conflict, setConflict] = useState<{ ids: number[]; text: string } | null>(null);
  const [working, setWorking] = useState(false);
  const pick = async (ids: number[], force = false) => {
    setWorking(true);
    const out = await onPick(ids, force);
    setWorking(false);
    if (out !== 'ok' && out !== 'error') setConflict({ ids, text: out });
  };
  return (
    <div className="fixed inset-0 z-[60] grid place-items-center bg-black/50 p-4" onClick={onClose}>
      <div className="flex max-h-[90vh] w-full max-w-[760px] flex-col overflow-hidden rounded-2xl border border-line bg-surface-1 shadow-xl" onClick={e => e.stopPropagation()}>
        <div className="flex items-start gap-3 border-b border-line px-5 py-4">
          <div className="min-w-0 flex-1">
            <h3 className="text-[19px] font-bold text-t1">{k('seat.title', { name: r.customer_name })}</h3>
            <p className="text-[13px] text-t3">{k('seat.sub', { count: r.party_size })}</p>
          </div>
          <Btn size="icon" aria-label={k('close')} onClick={onClose}><X className="h-4 w-4" /></Btn>
        </div>
        {conflict ? (
          <div className="grid gap-3 px-5 py-5">
            <Note tone="warn">{conflict.text}</Note>
            <div className="flex gap-2">
              <Btn variant="primary" disabled={working} onClick={() => pick(conflict.ids, true)}>{k('seat.force')}</Btn>
              <Btn onClick={() => setConflict(null)}>{k('seat.other')}</Btn>
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-[repeat(auto-fill,minmax(130px,1fr))] auto-rows-max gap-2 overflow-y-auto px-5 py-4">
            {order.map(t => {
              const taken = busy.has(t.id);
              const small = t.capacity < r.party_size;
              return (
                <button key={t.id} type="button" disabled={taken || working} onClick={() => pick([t.id])}
                  className={`grid gap-0.5 rounded-xl border-2 px-3 py-2.5 text-left transition-colors disabled:cursor-not-allowed disabled:opacity-40 ${tablesOf(r).includes(t.id) ? 'border-blue-500' : 'border-line hover:border-blue-400'}`}>
                  <span className="text-[17px] font-bold text-t1">{k('tablePrefix')}{t.table_number}</span>
                  <span className="text-[12px] text-t3">{k('seat.seats', { count: t.capacity })} · {zoneName(t.zone_id)}</span>
                  <span className={`text-[12px] font-semibold ${taken ? 'text-red-600' : small ? 'text-amber-600' : 'text-emerald-600'}`}>
                    {taken ? k('seat.taken') : small ? k('seat.small') : k('seat.free')}
                  </span>
                </button>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
